<template>
  <div class="chart-wizard">
    <div class="chart-wizard__card">
      <!-- Model (Dataset) -->
      <div class="chart-wizard__field">
        <label class="chart-wizard__label">Modelo</label>
        <v-select
          v-model="localDataset"
          :options="datasetOptions"
          :reduce="opt => opt.value"
          :clearable="false"
          placeholder="Seleccionar modelo"
          label="label"
          class="chart-wizard__select"
          append-to-body
          :calculate-position="withPopper"
        />
      </div>

      <!-- Date Range -->
      <div v-if="showTimeRange" class="chart-wizard__field">
        <ChartTimeFilter
          :from="localFrom"
          :to="localTo"
          label="Rango de fechas"
          @update:from="localFrom = $event"
          @update:to="localTo = $event"
        />
      </div>

      <!-- Chart (View) -->
      <div class="chart-wizard__field">
        <label class="chart-wizard__label">Gráfica</label>
        <v-select
          v-model="localView"
          :options="viewOptions"
          :reduce="opt => opt.value"
          :clearable="false"
          :disabled="!localDataset"
          placeholder="Seleccionar gráfica"
          label="label"
          class="chart-wizard__select"
          append-to-body
          :calculate-position="withPopper"
        />
      </div>

      <!-- Continue -->
      <button
        class="chart-wizard__btn"
        :disabled="!canContinue"
        @click="onContinue"
      >
        Continuar
      </button>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api'
import vSelect from 'vue-select'
import popperHelper from '@/libs/popper-helper'
import { getDatasetDefaultFrom, getDatasetModule } from '../config/datasets'
import { DEFAULT_TIME_RANGE_FROM, isInvertedRange } from '../utils/timeUtils'
import ChartTimeFilter from './ChartTimeFilter.vue'

export default {
  name: 'ChartSetupWizard',

  components: { vSelect, ChartTimeFilter },

  props: {
    datasetOptions: { type: Array, default: () => [] },
    viewOptions: { type: Array, default: () => [] },
    dateGroupingPresets: { type: Array, default: () => [] },
    t: { type: Function, required: true },
  },

  setup(props, { emit }) {
    const localDataset = ref('')
    const localView = ref('')
    const localFreq = ref('')

    // Time range, edited through the same control as the panel's.
    const localFrom = ref(DEFAULT_TIME_RANGE_FROM)
    const localTo = ref('')

    // A dataset whose time filter is unusable declares omitTimeRange: no date
    // controls, and its chart travels with no from/to (see useChartUrl).
    const showTimeRange = computed(() => !getDatasetModule(localDataset.value)?.omitTimeRange)

    // Reset view and freq when dataset changes
    watch(() => localDataset.value, () => {
      localView.value = ''
      localFreq.value = ''
      // Each dataset brings its own default window (getDatasetDefaultFrom).
      localFrom.value = getDatasetDefaultFrom(localDataset.value)
      localTo.value = ''
      emit('update:dataset', localDataset.value)
    })

    const canContinue = computed(() =>
      localDataset.value !== '' &&
      localView.value !== '' &&
      !(showTimeRange.value && isInvertedRange(localFrom.value, localTo.value))
    )

    const onContinue = () => {
      if (!canContinue.value) return
      emit('continue', {
        dataset: localDataset.value,
        view: localView.value,
        from: showTimeRange.value ? localFrom.value : '',
        to: showTimeRange.value ? localTo.value : '',
        freq: localFreq.value,
      })
    }

    return {
      localDataset,
      localView,
      localFreq,
      localFrom,
      localTo,
      canContinue,
      showTimeRange,
      onContinue,
      withPopper: popperHelper.withPopper,
    }
  },
}
</script>

<style scoped>
.chart-wizard {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  padding: 24px;
}

.chart-wizard__card {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chart-wizard__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chart-wizard__label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.7;
}

.chart-wizard__optional {
  font-weight: 400;
  text-transform: none;
  opacity: 0.5;
  font-size: 0.72rem;
}

.chart-wizard__select {
  font-size: 0.9rem;
}

/* ─── Continue button ─── */
.chart-wizard__btn {
  margin-top: 8px;
  padding: 10px 24px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  background: #02255a;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  align-self: flex-start;
}

.chart-wizard__btn:hover:not(:disabled) {
  background: #033a8a;
}

.chart-wizard__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ─── Light mode overrides ─── */
:root .charts-embedder:not(.charts-embedder--dark) .chart-wizard__label {
  color: #555;
}
</style>
